<template>
  <div class="quillContainer variableRichEditor">
    <div ref="richContainerRef" class="richZh"></div>
    <div class="toolbar">
      <t-button
        size="small"
        variant="text"
        theme="primary"
        @click="insertVariableSelector(infoDefaultValue)"
      >
        {{ $t("插入信息") }}
      </t-button>
      <!-- <t-button
        size="small"
        variant="text"
        theme="primary"
        @click="insertVariableSelector(systemDefaultValue)"
      >
        {{ $t("插入变量") }}
      </t-button> -->
    </div>
  </div>
  <t-dialog
    v-model:visible="dialogVisible"
    width="300"
    :header="false"
    class="variable-dialog"
    @confirm="handleConfirm"
  >
    <p v-if="variable.type === 'delete'">{{ t("确认删除？") }}</p>
    <t-input
      v-else
      v-model="variable.value"
      :placeholder="t('请输入变量key')"
      maxlength="100"
    />
  </t-dialog>
</template>

<script setup lang="ts">
import { ref, onMounted, watch, h, render, inject, reactive, Ref } from "vue";
import Quill from "quill";
import { cloneDeep, find, trim } from "lodash-es";
import { t } from "@/utils/i18n";
import VariableSelector from "./variable-selector.vue";
import { transformDeltaToString, transformStringToDelta } from "./util";
import { JudgeType } from "@/types/conditionJudgeNodeType";

const infoDefaultValue = `\${${JudgeType.INFO}-}`;
const systemDefaultValue = `\${${JudgeType.SYSTEM}-}`;
const dialogVisible = ref(false);
const variable = reactive<{
  value: string;
  type: string;
  index: number;
}>({
  value: "",
  type: "",
  index: 0,
});

// 导入必要的 Quill 模块
const Embed = Quill.import("blots/embed") as any;

// 创建变量选择器 Blot
class VariableSelectorBlot extends Embed {
  static blotName = "variableSelector";
  static tagName = "span";
  static className = "editor-variable-selector";

  static create(value: string) {
    const node = super.create();
    node.setAttribute("data-value", value);
    // type 是 ${xxxx-bbbb} 中的 xxxx
    const type = value.match(/\$\{([^}]+)\}/)?.[1].split("-")[0];
    // 创建变量选择器组件
    const selectorVNode = h(VariableSelector, {
      type: Number(type),
      defaultValue: value,
      variables: props.variables ?? [],
      onSelect: (variable) => {
        if (!quill) return;
        // 替换变量
        node.setAttribute("data-value", variable);
      },
      onClickButton: (newValue) => {
        dialogVisible.value = true;
        if (newValue.type !== "create") {
          variable.value = newValue.option.label;
        } else {
          variable.value = "";
        }
        variable.type = newValue.type;
        variable.index = newValue.index;
      },
    });

    // 渲染组件到容器中
    render(selectorVNode, node);
    return node;
  }

  static value(node: HTMLElement) {
    return node.getAttribute("data-value");
  }
}

// 注册自定义 Blots
Quill.register(VariableSelectorBlot);

const variablePool = inject<Ref<string[]>>("variablePool", ref<string[]>([]));
const changedVar = inject<Ref<{ oldValue: string; newValue: string }>>(
  "changedVar",
  ref<{ oldValue: string; newValue: string }>({ oldValue: "", newValue: "" }),
);
const updateVariablePool = inject<
  (
    v: string[],
    extra?: {
      index: number;
      type: string;
      oldValue: string;
      newValue: string;
    },
  ) => void
>("updateVariablePool");
const handleConfirm = () => {
  const _arr = cloneDeep(variablePool.value);
  const { value, type, index } = variable;
  if (!trim(value) && type !== "delete") return;
  if (type === "create" && find(_arr, (v) => v === trim(value))) {
    dialogVisible.value = false;
    return;
  }
  if (type === "create") {
    _arr.push(trim(value));
  }
  if (type === "delete") {
    _arr.splice(index, 1);
  }
  if (type === "edit") {
    _arr[index] = trim(value);
  }
  updateVariablePool?.(_arr, {
    index,
    type,
    oldValue: variablePool.value[index],
    newValue: type === "delete" ? "" : value,
  });
  dialogVisible.value = false;
};

const props = defineProps<{
  modelValue: string;
  variables?: Variable[];
}>();

const emit = defineEmits(["update:modelValue", "change"]);

let quill: Quill | null = null;

// 定义变量类型
interface Variable {
  id: string;
  value: string;
  label: string;
  type: JudgeType;
  children?: Variable[];
}

// 插入变量选择器
const insertVariableSelector = (type: string) => {
  if (!quill) return;

  const range = quill.getSelection(true);
  if (range) {
    quill.insertEmbed(range.index, "variableSelector", type);
    // 额外插入一个空格，解决拼写中文时，光标会出现在变量选择器后面的问题
    quill.insertText(range.index + 1, " ", Quill.sources.API);
    quill.setSelection(range.index + 2, 0);
  }
};

const richContainerRef = ref<HTMLDivElement>();

const placeholder = t("添加【信息】后描述他们之间的判断关系，帮助大模型理解");

// 初始化编辑器
onMounted(() => {
  if (!richContainerRef.value) return;
  quill = new Quill(richContainerRef.value, {
    theme: "snow",
    readOnly: false,
    bounds: ".flow-t-drawer__body",
    modules: {
      toolbar: false,
    },
    placeholder,
  });

  // 设置初始内容
  if (props.modelValue) {
    quill.setContents(transformStringToDelta(props.modelValue));
  }

  quill.root.addEventListener("input", () => {
    if (!quill) return;
    if (
      quill.root.innerText !== "\n" &&
      quill.root.classList.contains("ql-blank")
    ) {
      quill.root.classList.toggle("ql-blank", false);
    }
  });
  // 监听内容变化
  quill.on("editor-change", () => {
    if (!quill) return;
    const content = quill.root.innerHTML;
    emit("update:modelValue", transformDeltaToString(quill.getContents()));
    emit("change", {
      html: transformDeltaToString(quill.getContents()),
      content: quill.getContents(),
      text: quill.getText(),
    });
  });
});

// 监听modelValue变化
watch(
  () => props.modelValue,
  (newValue) => {
    if (quill && newValue !== transformDeltaToString(quill.getContents())) {
      quill.setContents(transformStringToDelta(newValue));
    }
  },
);
</script>

<style lang="less">
.variableRichEditor {
  display: flex;
  flex-direction: column;
  border: 1px solid #e5e5e5;
  background: #ffffff;
  .toolbar {
    padding: 5px 7px;
  }
  .ql-container {
    border: none !important;
    min-height: 100px;
  }
  .ql-editor.ql-blank::before {
    // 不要斜体
    font-style: normal;
  }
}

/* 选中文本的背景色 */
.ql-editor ::selection {
  background: rgba(28, 102, 229, 0.1); /* 使用相同颜色但带透明度的背景 */
}
</style>
